import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, User, LogOut, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isOverHero, setIsOverHero] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const { locale, setLocale, t } = useLanguage();
  const isHome = location.pathname === '/';

  const navItems = [
    { labelKey: 'header.navHero', href: '#hero' },
    { labelKey: 'header.navUeber', href: '#ueber' },
    { labelKey: 'header.navAblauf', href: '#ablauf' },
    { labelKey: 'header.navPartner', href: '#aussteller' },
    { labelKey: 'header.navFloorplan', href: '#floorplan' },
    { labelKey: 'header.navSocialWall', href: '#social-wall' },
    { labelKey: 'header.navTeam', href: '/team', isPage: true },
    { labelKey: 'header.navKontakt', href: '#contact' }
  ];

  const scrollToSectionAfterUnlock = (href) => {
    setIsMenuOpen(false);
    // Body uses position:fixed while menu is open — wait for effect to restore scroll
    window.setTimeout(() => {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 80);
  };

  const handleNavClick = (item) => {
    if (item.isPage) {
      navigate(item.href);
      setIsMenuOpen(false);
      return;
    }
    if (isHome) {
      scrollToSectionAfterUnlock(item.href);
    } else {
      navigate('/');
      setIsMenuOpen(false);
      window.setTimeout(() => {
        const element = document.querySelector(item.href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const getDisplayName = () => {
    if (!user) return '';
    if (user.user_type === 'company') {
      return user.company_name || user.name || (locale === 'en' ? 'Company' : 'Firma');
    }
    return user.first_name && user.last_name
      ? `${user.first_name} ${user.last_name}`
      : user.name || (locale === 'en' ? 'User' : 'User');
  };

  useEffect(() => {
    const handleScroll = () => {
      const heroEl = document.getElementById('hero');
      if (heroEl) {
        const heroBottom = heroEl.offsetTop + heroEl.offsetHeight;
        setIsOverHero(window.scrollY < heroBottom - 100);
      } else {
        setIsOverHero(window.scrollY < window.innerHeight * 0.8);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isTransparent = isHome && isOverHero;
  const hasAdminAccess =
    Boolean(user?.is_admin || user?.is_manager || user?.is_ticket_controller);

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsMenuOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isMenuOpen]);

  return (
    <>
      {/* Floating Menu Button */}
      <button
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        className={`fixed top-4 right-4 z-[60] p-3 min-h-[48px] min-w-[48px] rounded-xl transition-all duration-200 ${
          isTransparent
            ? 'bg-black/30 hover:bg-black/40 text-white border border-white/30'
            : 'bg-ngbc-green hover:bg-ngbc-green/90 text-white border border-ngbc-mint/50 shadow-lg'
        }`}
        aria-label={isMenuOpen ? t('header.menuClose') : t('header.menuOpen')}
      >
        {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Overlay */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 z-[55] bg-black/40"
          style={{ 
            transition: 'opacity 200ms ease-out',
            willChange: 'opacity'
          }}
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      {/* Side Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-80 max-w-[85vw] z-[70] ${
          isMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          transition: 'transform 250ms cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'transform'
        }}
      >
        <div className="h-full flex flex-col bg-ngbc-green/95 backdrop-blur-xl border-l border-white/20 shadow-2xl min-h-0">
          {/* Menu Header — fixed at top */}
          <div className="flex-shrink-0 p-6 border-b border-white/10">
            <div className="flex items-center gap-3">
              <img
                src="/logo.jpg"
                alt="Next Gen Business Cup Logo"
                className="h-12 w-12 object-contain rounded-lg bg-white/10 p-1"
              />
              <span className="text-xl font-bold text-white">
                Next Gen <span className="italic font-normal">Business</span> Cup
              </span>
            </div>
          </div>

          {/* Scrollable middle — never hidden under footer */}
          <div
            className={`flex-1 overflow-y-auto min-h-0 overscroll-contain ${
              hasAdminAccess ? 'pb-2' : ''
            }`}
          >
            {/* Navigation */}
            <nav className="p-4">
              <div className="space-y-1">
                {navItems.map((item) => (
                  <button
                    key={item.labelKey}
                    onClick={() => handleNavClick(item)}
                    className="w-full text-left px-4 py-3.5 min-h-[48px] rounded-xl text-white/90 hover:bg-white/10 hover:text-white font-medium transition-all flex items-center"
                  >
                    {t(item.labelKey)}
                  </button>
                ))}
              </div>
            </nav>

            <div className="mx-4 border-t border-white/10" />

            {/* Language Switcher */}
            <div className="p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-white/50 mb-3 px-4">
                {locale === 'en' ? 'Language' : 'Sprache'}
              </p>
              <div className="flex gap-2 px-4">
                <button
                  type="button"
                  onClick={() => setLocale('de')}
                  className={`flex-1 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                    locale === 'de'
                      ? 'bg-white text-ngbc-green'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  DE
                </button>
                <button
                  type="button"
                  onClick={() => setLocale('en')}
                  className={`flex-1 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                    locale === 'en'
                      ? 'bg-white text-ngbc-green'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  EN
                </button>
              </div>
            </div>

            <div className="mx-4 border-t border-white/10" />

            {/* User Section */}
            <div className={`p-4 ${hasAdminAccess ? 'pb-6' : 'pb-4'}`}>
              {isAuthenticated ? (
                <div className="space-y-1">
                  <div className="px-4 py-3 text-white/70 text-sm flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span>{getDisplayName()}</span>
                  </div>
                  {hasAdminAccess && (
                    <>
                      <button
                        onClick={() => { navigate('/portal'); setIsMenuOpen(false); }}
                        className="w-full text-left px-4 py-3 min-h-[44px] rounded-xl text-white/90 hover:bg-white/10 font-medium transition-all"
                      >
                        {t('header.meinPortal')}
                      </button>
                      <button
                        onClick={() => { navigate(user?.user_type === 'company' ? '/company-dashboard' : '/dashboard'); setIsMenuOpen(false); }}
                        className="w-full text-left px-4 py-3 min-h-[44px] rounded-xl text-white/90 hover:bg-white/10 font-medium transition-all"
                      >
                        {t('header.einstellungen')}
                      </button>
                    </>
                  )}
                  {hasAdminAccess && (
                    <>
                      <div className="border-t border-white/10 my-3" />
                      <button
                        type="button"
                        onClick={() => { navigate('/admin'); setIsMenuOpen(false); }}
                        className="w-full text-left px-4 py-4 min-h-[52px] rounded-xl bg-red-600/90 hover:bg-red-600 text-white font-semibold transition-all flex items-center gap-3 border-2 border-red-400/60 shadow-md"
                      >
                        <Shield className="w-5 h-5 shrink-0" />
                        <span>{t('header.adminPanel')}</span>
                      </button>
                    </>
                  )}
                  <div className="border-t border-white/10 my-2" />
                  <button
                    onClick={() => { logout(); navigate('/'); setIsMenuOpen(false); }}
                    className="w-full text-left px-4 py-3 min-h-[44px] rounded-xl text-white/90 hover:bg-white/10 font-medium transition-all"
                  >
                    <LogOut className="w-4 h-4 inline mr-2" />
                    {t('header.abmelden')}
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <button
                    onClick={() => { navigate('/?login=true'); setIsMenuOpen(false); }}
                    className="w-full py-3 rounded-xl bg-white text-ngbc-green font-bold hover:bg-white/90 transition-all"
                  >
                    {t('header.anmelden')}
                  </button>
                  <button
                    onClick={() => { navigate('/register'); setIsMenuOpen(false); }}
                    className="w-full py-3 rounded-xl bg-white/20 text-white font-semibold hover:bg-white/30 transition-all"
                  >
                    {t('header.registrieren')}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Footer — always below scroll area, never overlapping */}
          <div className="flex-shrink-0 p-4 border-t border-white/10 bg-ngbc-green/80">
            <p className="text-xs text-white/50 text-center">
              © 2026 Next Gen Business Cup
            </p>
          </div>
        </div>
      </div>
    </>
  );
};
